import React, { useRef, useState, useEffect } from "react";
import cx from "clsx";
import { Transition } from "react-transition-group";
import VisibilitySensor from "react-visibility-sensor";
import { Parallax } from "react-scroll-parallax";

import TextFromBottom from "@/components/shared/text/textFromBottom";
import { VIS_OFFSET, VIS_MIN_TOP } from "@/constants";

import globalStyles from "@/styles/global.module.scss";
import sectionStyles from "./sections.module.scss";
import styles from "./section6.module.scss";

const DELAY = 330;

const renderBgImg = props => {
  const { bgImg, bgImgY, bgImgClassName, disableParallax } = props;

  return bgImg ? (
    <div
      className={cx(
        globalStyles.background,
        styles.bgImgContainer,
        bgImgClassName
      )}
    >
      <Parallax y={bgImgY || ["-60px", "60px"]} disabled={disableParallax}>
        {bgImg}
      </Parallax>
    </div>
  ) : null;
};

const renderIcon = props => {
  const { icon, isVisible, disableParallax } = props;

  return icon ? (
    <Parallax y={["-5px", "-25px"]} disabled={!isVisible || disableParallax}>
      <div
        className={cx(
          styles.iconContainer,
          isVisible ? styles.visible : styles.hidden
        )}
      >
        {icon}
      </div>
    </Parallax>
  ) : null;
};

const renderEyebrow = props => (label, index = 0) => {
  const { animationReady, headerEyeBrowRef } = props;

  return (
    <Transition
      key={`section6-eyebrow-${index}`}
      enter
      exit
      in={animationReady}
      timeout={0}
    >
      {state => (
        <span
          ref={headerEyeBrowRef}
          className={cx(
            globalStyles.foreground,
            globalStyles.opacityAnimation,
            globalStyles[state],
            styles.headerEyebrow
          )}
        >
          {label}
        </span>
      )}
    </Transition>
  );
};

const renderTitle = props => {
  const {
    isVisible,
    eyeBrowText,
    headerText,
    headerLarge,
    typeHeaderRef,
    titleClassName
  } = props;

  let eyebrow = null;
  if (eyeBrowText) {
    eyebrow = Array.isArray(eyeBrowText)
      ? eyeBrowText.map(renderEyebrow(props))
      : renderEyebrow(props)(eyeBrowText);
  }

  return (
    <h3
      ref={typeHeaderRef}
      className={cx(
        sectionStyles.title,
        globalStyles.foreground,
        styles.header,
        headerLarge && styles.large
      )}
    >
      <span className={styles.headerInner}>
        <div className={styles.eyebrowContainer}>{eyebrow}</div>

        {headerText
          ? headerText.map((value, index) => (
              <TextFromBottom
                key={`section6-${index}`}
                className={cx(styles.headerText, titleClassName)}
                visible={isVisible}
                delayMs={(index + 1) * 180}
              >
                {value}
              </TextFromBottom>
            ))
          : null}
      </span>
    </h3>
  );
};

const Section6 = props => {
  const { isDesktop, containerClassName, innerClassName } = props;

  const typeHeaderRef = useRef(null);
  const headerEyeBrowRef = useRef(null);

  const [hasMounted, setHasMounted] = useState(false);
  const [isVisible, setVisible] = useState(false);
  const [animationReady, setAnimationReady] = useState(false);
  const setIsVisible = visible =>
    visible && !isVisible ? setVisible(true) : null;
  const memoSetAnimationReady = val => () => setAnimationReady(val);

  useEffect(() => {
    if (!hasMounted) setHasMounted(true);

    if (!isVisible) return;

    const timeout = !animationReady
      ? setTimeout(memoSetAnimationReady(true), DELAY)
      : null;

    // Unmount
    return () => {
      clearTimeout(timeout);
    };
  }, [isVisible]); // eslint-disable-line

  const childProps = { ...props, hasMounted, isVisible };

  return (
    <VisibilitySensor
      delayedCall
      partialVisibility
      offset={VIS_OFFSET}
      minTopValue={VIS_MIN_TOP}
      onChange={setIsVisible}
    >
      <div
        className={cx(
          sectionStyles.section,
          styles.container,
          containerClassName
        )}
      >
        {renderBgImg(childProps)}
        <div
          className={cx(
            isDesktop && globalStyles.contentPaddingH,
            globalStyles.foreground,
            styles.inner,
            innerClassName
          )}
        >
          {renderIcon(childProps)}
          {renderTitle({
            ...childProps,
            animationReady,
            typeHeaderRef,
            headerEyeBrowRef
          })}
        </div>
      </div>
    </VisibilitySensor>
  );
};

export default Section6;
